/**
 * Paragens geograficamente impossíveis.
 *
 * Uma rota de cidade não pode conter uma paragem a dezenas de quilómetros das
 * restantes: consome o dia inteiro em deslocação (ex.: 458 min entre duas
 * visitas) e destrói o horário. Este módulo remove essas paragens ANTES da
 * barreira final, usando apenas geometria — sem IO, sem relógio.
 *
 * Regra: uma paragem opcional cai quando está simultaneamente
 *  - acima do raio absoluto (`maxRadiusKm`), e
 *  - muito acima da distância típica das restantes paragens ao centro.
 * Paragens obrigatórias (pedidas explicitamente) nunca são removidas.
 */

export type GeoStop = {
  lat?: number;
  lng?: number;
  required?: boolean;
};

export type OutlierOptions = {
  /** Distância a partir da qual uma paragem é candidata a outlier. */
  maxRadiusKm?: number;
  /** Múltiplo da distância mediana que ainda se considera normal. */
  spreadFactor?: number;
};

const DEFAULT_MAX_RADIUS_KM = 20;
const DEFAULT_SPREAD_FACTOR = 3;

const toRad = (deg: number) => (deg * Math.PI) / 180;

/** Distância aproximada em quilómetros (haversine). */
export function distanceKm(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number },
): number {
  const R = 6371;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

const median = (values: number[]): number => {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
};

/** Centro robusto (mediana de latitudes/longitudes). */
export function medianCenter(stops: readonly GeoStop[]): { lat: number; lng: number } | null {
  const lats = stops.map((s) => s.lat).filter((n): n is number => Number.isFinite(n));
  const lngs = stops.map((s) => s.lng).filter((n): n is number => Number.isFinite(n));
  if (lats.length < 2 || lngs.length < 2) return null;
  return { lat: median(lats), lng: median(lngs) };
}

/**
 * Devolve as paragens sem os outliers geográficos. Sem coordenadas suficientes
 * nada é removido — nunca se corta às cegas.
 */
export function dropGeographicOutliers<T extends GeoStop>(
  stops: readonly T[],
  opts: OutlierOptions = {},
): T[] {
  const maxRadiusKm = opts.maxRadiusKm ?? DEFAULT_MAX_RADIUS_KM;
  const spreadFactor = opts.spreadFactor ?? DEFAULT_SPREAD_FACTOR;
  const center = medianCenter(stops);
  if (!center) return [...stops];
  const distances = stops.map((s) =>
    Number.isFinite(s.lat) && Number.isFinite(s.lng)
      ? distanceKm(center, { lat: s.lat as number, lng: s.lng as number })
      : null,
  );
  const known = distances.filter((d): d is number => d != null);
  if (known.length < 3) return [...stops];
  const typical = Math.max(0.5, median(known));
  const kept = stops.filter((stop, i) => {
    const d = distances[i];
    if (d == null || stop.required) return true;
    return !(d > maxRadiusKm && d > typical * spreadFactor);
  });
  // Nunca esvaziar a rota por causa desta regra.
  return kept.length ? kept : [...stops];
}
